import { Component, computed, signal, inject, OnInit, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';
import { Button } from 'primeng/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap } from 'rxjs/operators';
import { SidebarComponent } from '../components/sidebar/sidebar.component';
import { HeaderComponent } from '../components/header/header.component';
import { FooterComponent } from '../components/footer/footer.component';
import { PageMeta } from '../../core/models/menu-item.model';
import { User } from '../../core/models/user.model';
import { AuthService } from '../../core/services/auth.service';
import { MenuService } from '../../core/services/menu.service';
import { LoggerService } from '../../core/services/logger.service';
import { CONFIRM_DIALOG_STYLE } from '../../core/services/confirm.service';
import { NOTIFICATION_ICONS } from '../../core/config/ui.constants';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, HeaderComponent, FooterComponent, Toast, ConfirmDialog, Button],
  template: `
    <div class="layout-wrapper">
      <!-- Backdrop for mobile -->
      <!-- Fondo decorativo: cerrar por teclado se hace con el botón de alternar del header. -->
      <div
        class="layout-backdrop"
        role="presentation"
        [class.layout-backdrop--visible]="mobileSidebarOpen()"
        (click)="closeMobileSidebar()"
      ></div>

      <!-- Sidebar -->
      <app-sidebar
        [collapsed]="sidebarCollapsed()"
        [menuGroups]="menuGroups()"
        [currentUser]="currentUser()"
        (toggleCollapse)="toggleSidebar()"
        (itemSelected)="handleSidebarItemSelected()"
        class="layout-sidebar"
        [class.layout-sidebar--collapsed]="sidebarCollapsed()"
        [class.layout-sidebar--mobile-open]="mobileSidebarOpen()"
      />

      <!-- Main Content Area -->
      <div class="layout-main">
        <!-- Header -->
        <app-header
          [currentUser]="currentUser()"
          [pageTitle]="pageMeta().title"
          [pageIcon]="pageMeta().icon"
          [pageDescription]="pageMeta().description"
          (toggleSidebar)="handleToggleSidebar()"
          class="layout-header"
        />

        <!-- Scrollable Content -->
        <main class="layout-content">
          <router-outlet />
          <app-footer class="layout-footer" />
        </main>

        <!-- Única instancia de avisos; hereda el ancho del área de contenido. -->
        <p-toast position="bottom-center" [style.bottom]="toastBottomOffset" [style.left]="toastCenter">
          <ng-template #message let-message let-closeFn="closeFn">
            <p-button
              styleClass="rbi-toast-dismiss"
              [icon]="notificationIcons[message.severity]"
              severity="secondary"
              [text]="true"
              [rounded]="true"
              [disabled]="message.closable === false"
              ariaLabel="Cerrar alerta desde el icono"
              (onClick)="closeFn($event)" />
            <div class="p-toast-message-text">
              <span class="p-toast-summary">{{ message.summary }}</span>
              <span class="p-toast-detail">{{ message.detail }}</span>
            </div>
          </ng-template>
        </p-toast>
      </div>
    </div>

    <!-- Única instancia de confirmación (ConfirmService). -->
    <p-confirmdialog [style]="confirmStyle" />
  `,
  styleUrl: './main-layout.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly menuService = inject(MenuService);
  private readonly logger = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly confirmStyle = CONFIRM_DIALOG_STYLE;
  readonly toastBottomOffset = 'var(--toast-bottom-offset)';
  readonly toastCenter = 'var(--toast-center)';
  readonly notificationIcons = NOTIFICATION_ICONS;

  readonly sidebarCollapsed = signal(false);
  readonly mobileSidebarOpen = signal(false);
  /** El mismo signal del servicio: si el menú se recarga (p. ej. al reordenarlo), se ve al instante. */
  readonly menuGroups = this.menuService.menuGroups.asReadonly();
  readonly currentUser = computed<User | null>(() => this.authService.currentUser());
  readonly pageMeta = signal<PageMeta>({ title: '', icon: '' });

  ngOnInit(): void {
    this.listenForRouteChanges();
    this.loadInitialData();
  }

  handleToggleSidebar(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.mobileSidebarOpen.update((v) => !v);
    } else {
      this.sidebarCollapsed.update((v) => !v);
    }
  }

  toggleSidebar(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.mobileSidebarOpen.update((value) => !value);
    } else {
      this.sidebarCollapsed.update((value) => !value);
    }
  }

  closeMobileSidebar(): void {
    this.mobileSidebarOpen.set(false);
  }

  handleSidebarItemSelected(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.closeMobileSidebar();
    }
  }

  private loadInitialData(): void {
    this.authService.loadCurrentUser()
      .pipe(
        switchMap(() => this.menuService.loadMenuItems()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.pageMeta.set(this.menuService.resolvePageMeta(this.router.url));
        },
        error: (err) => {
          this.logger.error('MainLayout: Failed to load initial data', err);
        },
      });
  }

  private listenForRouteChanges(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        const navigation = event as NavigationEnd;
        this.pageMeta.set(this.menuService.resolvePageMeta(navigation.urlAfterRedirects));
      });
  }
}
